import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of, tap } from 'rxjs';

interface CacheEntry {
  response: HttpResponse<any>;
  timestamp: number;
}

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Only these API patterns will be cached
const CACHEABLE_PATTERNS = [
  '/api/academic/schools',
  '/api/academic/school-years',
  '/api/classes',
];

@Injectable({ providedIn: 'root' })
export class CacheInterceptor implements HttpInterceptor {

  private cache = new Map<string, CacheEntry>();

  clearAll(): void {
    this.cache.clear();
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (request.method !== 'GET') {
      this.invalidateRelatedCache(request.url);
      return next.handle(request);
    }

    if (!this.isCacheable(request.url)) {
      return next.handle(request);
    }

    const cacheKey = request.urlWithParams;
    const cached = this.cache.get(cacheKey);

    if (cached && (Date.now() - cached.timestamp) < DEFAULT_TTL_MS) {
      return of(cached.response.clone());
    }

    return next.handle(request).pipe(
      tap(event => {
        if (event instanceof HttpResponse && event.status === 200) {
          this.cache.set(cacheKey, { response: event.clone(), timestamp: Date.now() });
        }
      })
    );
  }

  private isCacheable(url: string): boolean {
    return CACHEABLE_PATTERNS.some(pattern => url.includes(pattern));
  }

  private invalidateRelatedCache(mutationUrl: string): void {
    const basePath = this.extractBasePath(mutationUrl);
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (key.includes(basePath)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  private extractBasePath(url: string): string {
    const apiIndex = url.indexOf('/api/');
    if (apiIndex === -1) return url;
    const afterApi = url.substring(apiIndex + 5);
    const firstSegment = afterApi.split('/')[0];
    return '/api/' + firstSegment;
  }
}
